import React, { useCallback, useEffect } from 'react'
import { MetaTags } from '../../meta-tags'
import MtLogo from '../../logos/mindtools_logo_primary_rgb.svg'
import { getWordCountFromBody } from './util'
import { getCategoryPathForBreadcrumb } from '../../../helpers/url'

const ContentMetaTags = ({ content }) => {
  const {
    title,
    subtitle,
    author = { name: 'MindTools Content Team' },
    readTime,
    metaTitle,
    metaDescription,
    metaTags = [],
    image,
    createdAt,
    updatedAt,
    keywords,
    mainCategoryName,
    body,
    locationId,
    staticUrl
  } = content

  let { canonicalLink } = content
  canonicalLink =
    canonicalLink || `${window.location.origin}/${locationId}/${staticUrl}`

  const finalTitle = metaTitle ?? title + ' - ' + subtitle.html

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalLink
    },
    author: [
      {
        '@type': 'Organization',
        name: 'Mindtools',
        url: 'https://www.mindtools.com/',
        logo: {
          '@type': 'ImageObject',
          url: '/static/media/mindtools_logo_primary_rgb.e0d130c0a72de3360c5de51f1fa34721.svg'
        }
      }
    ],
    url: canonicalLink,
    headline: title,
    alternativeHeadline: subtitle.html,
    image: image?.imgUrl,
    timeRequired: `PT${readTime}M`,
    inLanguage: 'en-US',
    isFamilyFriendly: 'true',
    copyrightHolder: 'Mindtools',
    publisher: {
      '@type': 'Organization',
      name: 'Mindtools',
      url: 'https://www.mindtools.com/',
      logo: {
        '@type': 'ImageObject',
        url: MtLogo
      }
    },
    genre: mainCategoryName,
    wordCount: getWordCountFromBody(body),
    isAccessibleForFree: false,
    hasPart: {
      '@type': 'WebPageElement',
      isAccessibleForFree: false,
      cssSelector: '.paywall'
    }
  }

  const generateArticleBreadCrumb = useCallback((content) => {
    if (!content.categoryPath || !content.categoryPath.length) {
      return ''
    }

    const categoryBreadCrumbs = content.categoryPath.reduce(
      (acc, category, idx) => {
        if (category.parentFriendlyUrl !== 'ROOT_CATEGORY_PARENT') {
          const schema = {
            '@type': 'ListItem',
            position: acc.length + 1,
            name: category.name,
            item: `${window.location.origin}${getCategoryPathForBreadcrumb(
              category
            )}`
          }
          return [...acc, schema]
        }
        return acc
      },
      []
    )

    return JSON.stringify(
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          ...categoryBreadCrumbs,
          {
            '@type': 'ListItem',
            position: categoryBreadCrumbs.length + 1,
            name: content.title
          }
        ]
      },
      null,
      2
    )
  }, [])

  // Conditionally render breadcrumbs on the content page.
  useEffect(() => {
    const elementId = 'content-breadcrumbs-schema'
    const element = document.getElementById(elementId)

    if (element) {
      element.parentNode.removeChild(element)
    }

    if (content.categoryPath && content.categoryPath.length) {
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.id = elementId
      script.innerHTML = generateArticleBreadCrumb(content)

      if (document.body) {
        document.head.appendChild(script)
      }
    }
  }, [content, generateArticleBreadCrumb])

  return (
    <MetaTags
      title={finalTitle}
      description={metaDescription}
      canonicalLink={canonicalLink}
      metaTags={metaTags}
      ogTags={{
        'og:type': 'article',
        'og:image': image?.imgUrl,
        'article:author': author.name,
        'article:modified_time': updatedAt,
        'article:published_time': createdAt,
        'article:tag': keywords
      }}
    >
      <meta name='robots' content='noarchive' />
      <script type='application/ld+json'>
        {JSON.stringify(jsonLd, null, 2)}
      </script>
    </MetaTags>
  )
}

export default ContentMetaTags
